import { memo, useMemo } from 'react';
import { Avatar, Badge, Button, Dropdown, Tooltip } from '@aimsos/ui';

import EntityHeaderPrimaryCta from './EntityHeaderPrimaryCta.jsx';
import EntityHeaderSkeleton from './EntityHeaderSkeleton.jsx';
import { typeAccentClass, typeInitials } from '../utils/typeInitials.js';

import { Info, MoreVertical } from 'lucide-react';

const MAX_VISIBLE_TAGS = 2;

/**
 * The rows that fold away when the header compresses. `overflow` is hidden ONLY
 * while folded: kept on permanently it would clip the metadata tooltips.
 */
const foldClass = (compact) =>
  [
    'w-full transition-all duration-200',
    compact ? 'max-h-0 overflow-hidden opacity-0' : 'max-h-40 overflow-visible opacity-100',
  ].join(' ');

/**
 * Entity Header — Figma DS AIMS OS node 19815:101548 ("Entity Header
 * Component", node 19890:11422 for the default/desktop variant).
 *
 * Identity card for a Unified Entity Profile. It identifies the entity you
 * are looking at and surfaces what needs attention. It carries no detail —
 * detail lives in the tabs below. It answers four questions, in this order:
 *
 *   1. What is this?        — avatar/highlight icon + title
 *   2. Where does it sit?    — source
 *   3. What is its status?   — state badge
 *   4. What needs attention? — tags
 *
 * It does not answer WHY — that is the Overview's job.
 *
 * IT ALWAYS SITS IN A CARD CONTAINER. This component has no surface, border,
 * radius or padding of its own — those come from wrapping it in
 * `<CartContainer size="L">` at the call site. A header with its own
 * background here would produce a box within a box. It is FILL width inside
 * that slot and never sets its own width.
 *
 * `compact` is the pinned state the page asks for once the profile has been
 * scrolled (CT-5526): identity, source, tags, status and the actions stay; the
 * description and the metadata row fold away. They are FOLDED, not unmounted —
 * collapsed by CSS and `inert`, the same rule the list pages follow for their
 * toolbar — so expanding again never re-creates them and Tab never lands in
 * content nobody can see.
 *
 * Presentational only — no data fetching, no `entityType` branch. Every
 * section (source, tags, status, description, secondary metadata, the three
 * actions, the running `workflow` chip of CT-5713) is present only when its prop is given, so a thinly mapped entity
 * renders sparse rather than with empty chrome.
 *
 * The primary action comes either as `primaryCta` (a plain `{ label, onClick }`
 * button) or as `primaryAction`, a ready node for an action that owns its own
 * state — Ask (`ProfileAskButton`, CT-5721) follows the shell chat, and as a
 * node only it re-renders with it. `primaryAction` wins when both are given.
 */
const EntityHeader = memo(function EntityHeader({
  loading = false,
  compact = false,
  avatar,
  title,
  source,
  tags,
  status,
  description,
  secondaryMeta,
  onInfoClick,
  secondaryCta,
  primaryCta,
  primaryAction,
  menu,
  workflow,
}) {
  const initials = useMemo(
    () => avatar?.initials ?? typeInitials(title ?? ''),
    [avatar?.initials, title],
  );
  const accentClass = useMemo(() => typeAccentClass(title ?? ''), [title]);

  const visibleTags = tags?.slice(0, MAX_VISIBLE_TAGS);
  const hiddenTagCount = tags ? Math.max(0, tags.length - MAX_VISIBLE_TAGS) : 0;

  if (loading) return <EntityHeaderSkeleton compact={compact} />;

  return (
    <div
      className={[
        'flex w-full min-w-0 flex-col items-start transition-[gap] duration-200',
        compact ? 'gap-0' : 'gap-3',
      ].join(' ')}
      data-testid="entity-header"
      data-compact={compact ? 'true' : 'false'}
    >
      <div className="flex w-full items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <div className="flex shrink-0 items-center gap-2">
            <Avatar
              src={avatar?.src || undefined}
              alt={avatar?.alt ?? title ?? undefined}
              initials={initials}
              className={avatar?.src ? undefined : accentClass}
              size={compact ? 'md' : 'lg'}
            />
            <Tooltip content={title} triggerClassName="block min-w-0 max-w-[540px]">
              <h1
                data-testid="entity-header-title"
                className="truncate text-xl font-semibold text-content"
              >
                {title}
              </h1>
            </Tooltip>
            {workflow?.label ? (
              <Badge variant="purple" size="small">
                {workflow.label}
              </Badge>
            ) : null}
          </div>

          {source ? (
            <div className="flex h-8 shrink-0 items-center gap-2" data-testid="entity-header-source">
              <span className="text-content-tertiary" aria-hidden>
                &bull;
              </span>
              <div className="flex items-center gap-1">
                {source.icon ? <source.icon className="size-4 text-content-muted" /> : null}
                <span className="text-body-s font-medium text-content-secondary">{source.label}</span>
              </div>
            </div>
          ) : null}

          {tags?.length ? (
            <>
              <span className="h-4 w-px shrink-0 bg-content-tertiary" aria-hidden />
              <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="entity-header-tags">
                {visibleTags.map((tag) => (
                  <Badge key={tag.label} variant={tag.variant ?? 'default'} size="small">
                    {tag.label}
                  </Badge>
                ))}
                {hiddenTagCount > 0 ? (
                  <Badge variant="default" size="small">
                    +{hiddenTagCount}
                  </Badge>
                ) : null}
              </div>
            </>
          ) : null}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {onInfoClick ? (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={Info}
              onClick={onInfoClick}
              aria-label="About this record"
            />
          ) : null}

          {status ? (
            <Badge variant={status.variant ?? 'informative'} size="small">
              {status.label}
            </Badge>
          ) : null}

          {secondaryCta ? (
            <Button variant="secondary" size="sm" onClick={secondaryCta.onClick}>
              {secondaryCta.label}
            </Button>
          ) : null}

          {primaryAction ?? (primaryCta ? <EntityHeaderPrimaryCta cta={primaryCta} /> : null)}

          {menu ? (
            <Dropdown
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={MoreVertical}
                  aria-label="More actions"
                />
              }
            >
              {menu}
            </Dropdown>
          ) : null}
        </div>
      </div>

      {description ? (
        <div className={foldClass(compact)} inert={compact ? '' : undefined}>
          <p
            data-testid="entity-header-description"
            className="w-full truncate text-body-s font-medium text-content-secondary"
          >
            {description}
          </p>
        </div>
      ) : null}

      {secondaryMeta?.length ? (
        <div
          className={[foldClass(compact), 'flex flex-wrap items-center gap-4'].join(' ')}
          inert={compact ? '' : undefined}
          data-testid="entity-header-secondary-meta"
        >
          {secondaryMeta.map((item, index) => (
            <div key={item.label} className="flex items-center gap-4">
              {index > 0 ? (
                <span className="text-content-tertiary" aria-hidden>
                  &bull;
                </span>
              ) : null}
              <div className="flex items-center gap-1">
                {item.icon ? <item.icon className="size-4 text-content-muted" /> : null}
                <Tooltip content={item.label} triggerClassName="block min-w-0">
                  <span className="block max-w-[200px] truncate text-body-s font-medium text-content-secondary">
                    {item.label}
                  </span>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
});

export default EntityHeader;
